import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Phone, PhoneOff, Video, VideoOff, Mic, MicOff, 
  ArrowLeft, Loader2, MessageCircle, Volume2, VolumeX
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';

const VideoCall = () => {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  
  const [callStatus, setCallStatus] = useState('connecting'); // connecting, active, ended
  const [otherUser, setOtherUser] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOn, setIsVideoOn] = useState(true);
  const [isSpeakerOn, setIsSpeakerOn] = useState(true);
  const [callDuration, setCallDuration] = useState(0);
  
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const localStreamRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    initializeCall();
    return () => {
      cleanupCall();
    };
  }, []);

  useEffect(() => {
    if (callStatus === 'active') {
      timerRef.current = setInterval(() => {
        setCallDuration(prev => prev + 1);
      }, 1000);
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [callStatus]);

  const initializeCall = async () => {
    try {
      // Fetch match info to get other user
      const matchRes = await axios.get(`${API}/matches`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const match = matchRes.data.find(m => m.id === matchId);
      if (match?.other_user) {
        setOtherUser(match.other_user);
      }

      // Request camera/mic permissions
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true
      });
      
      localStreamRef.current = stream;
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }

      // Simulate connection delay
      setTimeout(() => {
        setCallStatus('active');
        toast.success('Opkald forbundet!');
      }, 2000);

    } catch (err) {
      console.error('Call initialization error:', err);
      if (err.name === 'NotAllowedError') {
        toast.error('Kamera/mikrofon adgang nægtet');
      } else {
        toast.error('Kunne ikke starte opkald');
      }
      setCallStatus('ended');
    }
  };

  const cleanupCall = () => {
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
  };

  const toggleMute = () => {
    if (localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsMuted(!isMuted);
      }
    }
  };

  const toggleVideo = () => {
    if (localStreamRef.current) {
      const videoTrack = localStreamRef.current.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setIsVideoOn(!isVideoOn);
      }
    }
  };

  const endCall = () => {
    cleanupCall();
    setCallStatus('ended');
    toast.info('Opkald afsluttet');
    setTimeout(() => {
      navigate(-1);
    }, 1000);
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="fixed inset-0 bg-black z-50">
      {/* Remote Video (Full screen) - Currently showing placeholder */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-900 to-black flex items-center justify-center">
        {callStatus === 'connecting' ? (
          <div className="text-center">
            <div className="w-32 h-32 rounded-full overflow-hidden mx-auto mb-6 border-4 border-primary">
              {otherUser?.photos?.[0] ? (
                <img
                  src={otherUser.photos[0].startsWith('/api') ? `${API.replace('/api', '')}${otherUser.photos[0]}` : otherUser.photos[0]}
                  alt={otherUser?.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-muted flex items-center justify-center">
                  <Video className="w-12 h-12 text-muted-foreground" />
                </div>
              )}
            </div>
            <Loader2 className="w-8 h-8 text-primary animate-spin mx-auto mb-4" />
            <p className="text-white text-xl font-display">Ringer til {otherUser?.name || 'bruger'}...</p>
          </div>
        ) : callStatus === 'active' ? (
          <div className="text-center">
            <div className="w-48 h-48 rounded-full overflow-hidden mx-auto mb-6 border-4 border-primary animate-pulse">
              {otherUser?.photos?.[0] ? (
                <img
                  src={otherUser.photos[0].startsWith('/api') ? `${API.replace('/api', '')}${otherUser.photos[0]}` : otherUser.photos[0]}
                  alt={otherUser?.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-muted flex items-center justify-center">
                  <Video className="w-16 h-16 text-muted-foreground" />
                </div>
              )}
            </div>
            <p className="text-white text-2xl font-display mb-2">{otherUser?.name}</p>
            <p className="text-white/70 text-lg">{formatDuration(callDuration)}</p>
            <p className="text-white/50 text-sm mt-4">
              Video opkald - Demo tilstand
            </p>
          </div>
        ) : (
          <div className="text-center">
            <PhoneOff className="w-16 h-16 text-destructive mx-auto mb-4" />
            <p className="text-white text-xl">Opkald afsluttet</p>
          </div>
        )}
      </div>

      {/* Local Video (Picture-in-picture) */}
      <motion.div
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="absolute top-6 right-6 w-32 h-48 rounded-2xl overflow-hidden border-2 border-white/30 shadow-xl"
      >
        <video
          ref={localVideoRef}
          autoPlay
          playsInline
          muted
          className={`w-full h-full object-cover ${!isVideoOn ? 'hidden' : ''}`}
        />
        {!isVideoOn && (
          <div className="w-full h-full bg-gray-800 flex items-center justify-center">
            <VideoOff className="w-8 h-8 text-white/50" />
          </div>
        )}
      </motion.div>

      {/* Top bar */}
      <div className="absolute top-6 left-6">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(-1)}
          className="text-white bg-white/10 hover:bg-white/20 rounded-full"
        >
          <ArrowLeft className="w-6 h-6" />
        </Button>
      </div>

      {/* Bottom controls */}
      <div className="absolute bottom-8 left-0 right-0">
        <div className="flex items-center justify-center gap-4">
          <Button
            variant="ghost"
            size="lg"
            onClick={toggleMute}
            className={`w-14 h-14 rounded-full ${
              isMuted ? 'bg-white text-black' : 'bg-white/10 text-white hover:bg-white/20'
            }`}
            data-testid="toggle-mute-btn"
          >
            {isMuted ? <MicOff className="w-6 h-6" /> : <Mic className="w-6 h-6" />}
          </Button>

          <Button
            variant="ghost"
            size="lg"
            onClick={toggleVideo}
            className={`w-14 h-14 rounded-full ${
              !isVideoOn ? 'bg-white text-black' : 'bg-white/10 text-white hover:bg-white/20'
            }`}
            data-testid="toggle-video-btn"
          >
            {isVideoOn ? <Video className="w-6 h-6" /> : <VideoOff className="w-6 h-6" />}
          </Button>

          <Button
            size="lg"
            onClick={endCall}
            className="w-16 h-16 rounded-full bg-destructive hover:bg-destructive/90"
            data-testid="end-call-btn"
          >
            <PhoneOff className="w-8 h-8 text-white" />
          </Button>

          <Button
            variant="ghost"
            size="lg"
            onClick={() => setIsSpeakerOn(!isSpeakerOn)}
            className={`w-14 h-14 rounded-full ${
              !isSpeakerOn ? 'bg-white text-black' : 'bg-white/10 text-white hover:bg-white/20'
            }`}
          >
            {isSpeakerOn ? <Volume2 className="w-6 h-6" /> : <VolumeX className="w-6 h-6" />}
          </Button>

          <Button
            variant="ghost"
            size="lg"
            onClick={() => navigate(`/chat/${otherUser?.id}`)}
            className="w-14 h-14 rounded-full bg-white/10 text-white hover:bg-white/20"
          >
            <MessageCircle className="w-6 h-6" />
          </Button>
        </div>
      </div>

      {/* Demo notice */}
      <div className="absolute bottom-24 left-0 right-0 text-center">
        <p className="text-white/40 text-xs">
          Demo tilstand - WebRTC peer-to-peer forbindelse kræver TURN/STUN server
        </p>
      </div>
    </div>
  );
};

export default VideoCall;
